<template>
  <div class="row">
    <!-- Deals by Stage -->
    <div class="col-xl-4 d-flex">
      <div class="card flex-fill">
        <div class="card-header">
          <div
            class="d-flex align-items-center justify-content-between flex-wrap row-gap-2"
          >
            <h5>Deals by Stage</h5>
            <small class="text-muted">Click a bar to filter</small>
          </div>
        </div>

        <div class="card-body pb-0">
          <div>
            <div class="d-flex align-items-center">
              <h3 class="me-2">{{ stageSummaryPct }}%</h3>
              <span
                class="badge badge-outline-success bg-success-transparent rounded-pill me-1"
              >
                {{ stageSummaryDelta }}%
              </span>
              <span class="text-muted">based on current range</span>
            </div>

            <div id="deals_stage">
              <apexchart
                type="bar"
                height="280"
                :options="stageOptions"
                :series="stageSeries"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Top Clients -->
    <div class="col-xl-4 d-flex">
      <div class="card flex-fill">
        <div class="card-header">
          <div
            class="d-flex align-items-center justify-content-between flex-wrap row-gap-2"
          >
            <h5>Deals By Clients/Companies</h5>
            <small class="text-muted">Click a company to search</small>
          </div>
        </div>

        <div class="card-body">
          <div v-if="!topClients.length" class="text-muted small">
            No client summary for selected filters.
          </div>

          <div
            v-for="c in topClients"
            :key="c.company_name"
            class="border border-dashed bg-transparent-light rounded p-2 mb-2 hover-pointer"
            @click="drillCompany(c.company_name)"
          >
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <a
                  href="javascript:void(0);"
                  class="avatar avatar-md rounded-circle bg-gray-100 flex-shrink-0 me-2"
                >
                  <span
                    class="d-flex align-items-center justify-content-center w-100 h-100"
                  >
                    <i class="ti ti-building"></i>
                  </span>
                </a>
                <div>
                  <h6 class="fw-medium mb-1">{{ c.company_name || "—" }}</h6>
                  <p class="text-truncate mb-0 text-muted">
                    {{ fmtNum(c.count) }} deals
                  </p>
                </div>
              </div>
              <div>
                <h6 class="mb-0">{{ fmtMoney(c.value, currency) }}</h6>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Top Deals Radar -->
    <div class="col-xl-4 d-flex">
      <div class="card flex-fill">
        <div class="card-header">
          <div
            class="d-flex align-items-center justify-content-between flex-wrap row-gap-2"
          >
            <h5>Top Deals</h5>
            <small class="text-muted">Dynamic series</small>
          </div>
        </div>
        <div class="card-body">
          <div class="text-center" v-if="radarCategories.length">
            <apexchart
              type="radar"
              height="200"
              :options="radarOptions"
              :series="radarSeries"
            />
          </div>

          <div v-else class="text-muted small">
            No radar data for selected filters.
          </div>

          <div class="mt-3">
            <h6 class="mb-3">Status</h6>
            <div class="d-flex align-items-center justify-content-between mb-2">
              <p class="f-13 mb-0">
                <i class="ti ti-circle-filled text-primary me-1"></i>Open
              </p>
              <p class="f-13 fw-medium text-gray-9">
                {{ fmtNum(kpi.open_deals) }}
              </p>
            </div>
            <div class="d-flex align-items-center justify-content-between mb-2">
              <p class="f-13 mb-0">
                <i class="ti ti-circle-filled text-success me-1"></i>Won
              </p>
              <p class="f-13 fw-medium text-gray-9">
                {{ fmtNum(kpi.won_deals) }}
              </p>
            </div>
            <div class="d-flex align-items-center justify-content-between">
              <p class="f-13 mb-0">
                <i class="ti ti-circle-filled text-danger me-1"></i>Lost
              </p>
              <p class="f-13 fw-medium text-gray-9">
                {{ fmtNum(kpi.lost_deals) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useDealDashboardStore } from "@/stores/dealDashboardStore";

const dash = useDealDashboardStore();

const topClients = computed(() => {
  return Array.isArray(dash.topClients)
    ? dash.topClients
    : [];
});


const kpi = computed(() => dash.kpis || {});
const currency = computed(() => dash.kpis?.currency || "KES");

const stageRows = computed(() =>
  (dash.dealsByStage || []).map((x) => ({
    stage: x.stage ?? x.stage_name ?? x.name ?? "Stage",
    count: Number(x.count ?? 0),
    value: Number(x.value ?? 0),
  }))
);

const stageSeries = computed(() => [
  { name: "Deals", data: stageRows.value.map((x) => x.count) },
]);

const stageOptions = computed(() => ({
  chart: {
    height: 280,
    type: "bar",
    stacked: false,
    toolbar: { show: false },
    events: {
      dataPointSelection: async (event, chartContext, config) => {
        const idx = config?.dataPointIndex ?? -1;
        const stageName = stageRows.value[idx]?.stage;
        if (!stageName) return;

        const ok = dash.setStageByName(stageName);
        if (ok) await dash.fetchDashboard();
      },
    },
  },
  colors: ["#FF6F28"],
  plotOptions: { bar: { horizontal: false, endingShape: "rounded" } },
  xaxis: {
    categories: stageRows.value.map((x) => x.stage),
    labels: { style: { colors: "#6B7280", fontSize: "13px" } },
  },
  yaxis: { labels: { style: { colors: "#6B7280", fontSize: "13px" } } },
  grid: { borderColor: "#E5E7EB", strokeDashArray: 5 },
  legend: { show: false },
  dataLabels: { enabled: false },
}));

// simple placeholders (since backend does not send "vs last year" yet)
const stageSummaryPct = computed(() => 100);
const stageSummaryDelta = computed(() => 0);

// radar
const radarCategories = computed(() => dash.topDealsRadar?.categories || []);
const radarSeries = computed(() => dash.topDealsRadar?.series || []);
const radarOptions = computed(() => ({
  chart: { height: 200, type: "radar" },
  stroke: { width: 2 },
  fill: { opacity: 1 },
  markers: { size: 0 },
  legend: { show: false },
  xaxis: { categories: radarCategories.value },
}));

function fmtNum(v) {
  return Number(v || 0).toLocaleString();
}
function fmtMoney(v, cur = "KES") {
  const n = Number(v || 0);
  return `${cur} ${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

async function drillCompany(company) {
  if (!company) return;
  dash.filters.q = company;
  await dash.fetchDashboard();
}
</script>

<style scoped>
.hover-pointer {
  cursor: pointer;
}
</style>
